export type ColumnSpec = { px: number } | { fr: number };

/**
 * `<col>` width values for a fixed-px + flexible mix — the same shape as a
 * CSS Grid `grid-template-columns` recipe (e.g. nazare-frontend's own
 * `RUNS_COLS`: `grid-cols-[96px_1fr_minmax(0,2fr)_140px_88px_120px]`), for
 * tables that need to match one exactly. A `<table>`'s own width algorithm
 * can't express this: plain percentages drift at different table widths
 * (only right at whatever reference width they were computed for), and
 * `fr` isn't a valid `<col>` width unit at all. `calc()` is — so the
 * flexible columns are expressed as "this fr-share of whatever's left
 * after the fixed columns", pixel-exact at any table width, same as real
 * CSS Grid.
 *
 * Pass the result to a `<colgroup>` alongside `table-layout: fixed` on the
 * `<table>` (fixed layout is what makes the browser honour `<col>` widths
 * instead of resizing columns to fit content).
 *
 * Shared so every table wanting exact columns uses the same math instead
 * of each page hand-rolling its own percentage approximation — see
 * `routes/admin/workflows/runs-columns.ts` for this project's own recipe.
 */
export function buildFixedColumnWidths(spec: readonly ColumnSpec[]): string[] {
  const fixedTotal = spec.reduce((sum, s) => ("px" in s ? sum + s.px : sum), 0);
  const totalFr = spec.reduce((sum, s) => ("fr" in s ? sum + s.fr : sum), 0);
  return spec.map((s) => {
    if ("px" in s) return `${s.px}px`;
    if (totalFr === 0) return "0px";
    return `calc((100% - ${fixedTotal}px) * ${s.fr} / ${totalFr})`;
  });
}
